import React, { useState, useEffect, useMemo } from 'react';
import {
  Paper, Typography, Box, CircularProgress, Alert, Tabs, Tab, Stack, ToggleButtonGroup, ToggleButton, FormControl, InputLabel, Select, MenuItem, Divider, FormControlLabel, Switch, Grid, Tooltip as MuiTooltip
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList, Cell
} from 'recharts';
import { useTranslation } from 'react-i18next';
import supabase from '../../../utils/supabase';

const SubfamilyComparisonChart = () => {
  const { t } = useTranslation(['analysis', 'common']);
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFamily, setActiveFamily] = useState('');
  const [chartType, setChartType] = useState('species'); // 'species' or 'genus'
  const [sortKey, setSortKey] = useState('subfamily_name'); // 'subfamily_name', 'db_count', 'taxapad_count', 'difference'
  const [checklistSource, setChecklistSource] = useState('taxapad_2016');
  const [includeSubspecies, setIncludeSubspecies] = useState(true);

  const checklistSources = useMemo(() => [
    { key: 'all', label: t('analysis:comparison.compareWith.all') },
    { key: 'taxapad_2016', label: 'Taxapad 2016' },
    { key: 'gbif_backbone', label: 'GBIF Backbone' },
    { key: 'checklist_bank', label: 'Checklist Bank' },
  ], [t]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: rpcError } = await supabase.rpc('get_checklist_comparison_stats');
        if (rpcError) throw rpcError;

        setAllData(data || []);
        if (data && data.length > 0) {
          // Find Ichneumonidae first, otherwise default to the first family
          const ichneumonidae = data.find(d => d.family_name === 'Ichneumonidae');
          setActiveFamily(ichneumonidae ? 'Ichneumonidae' : data[0].family_name);
        }
      } catch (err) {
        console.error("Failed to fetch comparison stats:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const { families, dataByFamily } = useMemo(() => {
    if (!allData.length) return { families: [], dataByFamily: new Map() };

    const familyMap = new Map();
    allData.forEach(item => {
      if (!familyMap.has(item.family_name)) {
        familyMap.set(item.family_name, []);
      }
      familyMap.get(item.family_name).push(item);
    });

    familyMap.forEach((subfamilies) => {
      subfamilies.sort((a, b) => a.subfamily_name.localeCompare(b.subfamily_name));
    });

    return {
      families: Array.from(familyMap.keys()).sort(),
      dataByFamily: familyMap,
    };
  }, [allData]);

  const handleFamilyChange = (_, newValue) => {
    setActiveFamily(newValue);
  };

  const handleChartTypeChange = (_, newValue) => {
    if (newValue) setChartType(newValue);
  };

  const handleSortChange = (event) => {
    setSortKey(event.target.value);
  };

  const handleChecklistChange = (_, newValue) => {
    if (newValue) setChecklistSource(newValue);
  };

  // "All"モードでは"Difference"は意味がないので、切り替わったら"species"に戻す
  useEffect(() => {
    if (checklistSource === 'all' && chartType === 'difference') {
      setChartType('species');
    }
  }, [checklistSource, chartType]);

  const countType = chartType === 'difference' ? 'species' : chartType;

  const activeChartData = useMemo(() => {
    const data = dataByFamily.get(activeFamily) || [];
    const enrichedData = data.map(item => {
      const dbSpeciesOnly = item.db_species_only_count || 0;
      const dbSubspecies = item.db_subspecies_count || 0;
      const dbGenus = item.db_genus_count || 0;
      const dbSpeciesTotal = dbSpeciesOnly + (includeSubspecies ? dbSubspecies : 0);
      const dbCount = countType === 'genus' ? dbGenus : dbSpeciesTotal;
      
      if (checklistSource === 'all') {
        const allStats = { db_count: dbCount };
        checklistSources.forEach(source => {
          if (source.key !== 'all') {
            const checklistData = item.checklist_stats?.[source.key] || {};
            const checklistSpecies = checklistData.species || 0;
            const checklistSubspecies = checklistData.subspecies || 0;
            const checklistGenus = checklistData.genera || 0;
            const checklistSpeciesTotal = checklistSpecies + (includeSubspecies ? checklistSubspecies : 0);
            allStats[`${source.key}_count`] = countType === 'genus' ? checklistGenus : checklistSpeciesTotal;
          }
        });
        return { ...item, ...allStats };
      } else {
        const checklistData = item.checklist_stats?.[checklistSource] || {};
        const checklistSpecies = checklistData.species || 0;
        const checklistSubspecies = checklistData.subspecies || 0;
        const checklistGenus = checklistData.genera || 0;
        const checklistSpeciesTotal = checklistSpecies + (includeSubspecies ? checklistSubspecies : 0);
        const checklistCount = countType === 'genus' ? checklistGenus : checklistSpeciesTotal;
        const difference = dbCount - checklistCount;
        return { ...item, db_count: dbCount, checklist_count: checklistCount, difference };
      }
    });

    enrichedData.sort((a, b) => {
      switch (sortKey) {
        case 'db_count':
          return b.db_count - a.db_count;
        case 'checklist_count':
          return b.checklist_count - a.checklist_count;
        case 'difference':
          return Math.abs(b.difference) - Math.abs(a.difference);
        case 'subfamily_name':
        default:
          return a.subfamily_name.localeCompare(b.subfamily_name);
      }
    });

    return enrichedData;
  }, [activeFamily, dataByFamily, chartType, sortKey, checklistSource, countType, checklistSources, includeSubspecies]);

  const chartHeight = Math.max(600, activeChartData.length * 40);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      if (checklistSource === 'all') {
        return (
          <Paper sx={{ p: 1.5, bgcolor: 'rgba(30, 30, 30, 0.9)', border: '1px solid #555' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{label}</Typography>
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              <Typography variant="body2">{t('analysis:comparison.thisDb')}: {data.db_count?.toLocaleString()}</Typography>
              <Divider sx={{ my: 0.5 }} />
              {checklistSources.filter(s => s.key !== 'all').map(source => (
                <Typography key={source.key} variant="body2">
                  {source.label}: {data[`${source.key}_count`]?.toLocaleString() ?? t('common:notAvailable')}
                </Typography>
              ))}
            </Stack>
          </Paper>
        );
      }

      const dbCount = data.db_count || 0;
      const checklistCount = data.checklist_count || 0;
      const difference = data.difference;
      const percentageChange = checklistCount > 0 ? ((difference / checklistCount) * 100).toFixed(1) : t('common:notAvailable');

      return (
        <Paper sx={{ p: 1.5, bgcolor: 'rgba(30, 30, 30, 0.9)', border: '1px solid #555' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{label}</Typography>
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            <Typography variant="body2">{t('analysis:comparison.thisDb')}: {dbCount.toLocaleString()}</Typography>
            <Typography variant="body2">{checklistSources.find(c => c.key === checklistSource)?.label || t('analysis:comparison.checklist')}: {checklistCount.toLocaleString()}</Typography>
            <Divider />
            <Typography variant="body2" sx={{ color: difference >= 0 ? '#82ca9d' : '#ff8a65' }}>
              {t('analysis:comparison.difference')}: {difference > 0 ? '+' : ''}{difference.toLocaleString()}
              {percentageChange !== t('common:notAvailable') && ` (${percentageChange > 0 ? '+' : ''}${percentageChange}%)`}
            </Typography>
          </Stack>
        </Paper>
      );
    }
    return null;
  };

  const renderChart = () => {
    if (checklistSource === 'all') {
      return (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart data={activeChartData} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis dataKey="subfamily_name" type="category" width={180} interval={0} tick={{ fill: '#e0e0e0' }} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255, 255, 255, 0.1)' }} />
            <Legend />
            <Bar dataKey="db_count" name={t('analysis:comparison.thisDbWithType', { type: countType })} fill="#82ca9d" />
            <Bar dataKey="taxapad_2016_count" name={`Taxapad 2016 (${countType})`} fill="#8884d8" />
            <Bar dataKey="gbif_backbone_count" name={`GBIF (${countType})`} fill="#ffc658" />
            <Bar dataKey="checklist_bank_count" name={`Checklist Bank (${countType})`} fill="#d0ed57" />
          </BarChart>
        </ResponsiveContainer>
      );
    }
    return (
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          data={activeChartData}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis dataKey="subfamily_name" type="category" width={180} interval={0} tick={{ fill: '#e0e0e0' }} />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255, 255, 255, 0.1)' }} />
          <Legend />
          {checklistSource !== 'all' && chartType === 'difference' ? (
            <Bar dataKey="difference" name={t('analysis:comparison.difference')} >
              {activeChartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.difference >= 0 ? '#82ca9d' : '#ff8a65'} />
              ))}
            </Bar>
          ) : (
            <>
              <Bar dataKey="checklist_count" name={`${checklistSources.find(c => c.key === checklistSource)?.label || t('analysis:comparison.checklist')} (${countType})`} fill="#8884d8" />
              <Bar dataKey="db_count" name={t('analysis:comparison.thisDbWithType', { type: countType })} fill="#82ca9d" />
            </>
          )}
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Paper elevation={4} sx={{ p: 3, borderRadius: 4, overflowX: "hidden" }}>
      <Paper variant="outlined" sx={{ p: 2, mt: 3, bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center" justifyContent="space-around">
          <Grid item xs={12} sm={6} md="auto">
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', pl: 1 }}>{t('analysis:comparison.compareWith.title')}</Typography>
              <ToggleButtonGroup color="primary" value={checklistSource} exclusive onChange={handleChecklistChange} aria-label="checklist source" size="small">
                {checklistSources.map(source => (
                  <ToggleButton key={source.key} value={source.key}>{source.label}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6} md="auto">
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', pl: 1 }}>{t('analysis:comparison.dataType.title')}</Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <ToggleButtonGroup color="primary" value={chartType} exclusive onChange={handleChartTypeChange} aria-label="chart type" size="small">
                  <ToggleButton value="species">{t('common:species')}</ToggleButton>
                  <ToggleButton value="genus">{t('common:genera')}</ToggleButton>
                  {checklistSource !== 'all' && <ToggleButton value="difference">{t('analysis:comparison.dataType.difference')}</ToggleButton>}
                </ToggleButtonGroup>
                <MuiTooltip title={t('analysis:comparison.includeSubspeciesTooltip')}>
                  <FormControlLabel
                    control={<Switch size="small" checked={includeSubspecies} onChange={(e) => setIncludeSubspecies(e.target.checked)} />}
                    label={t('analysis:comparison.withSubspecies')}
                    disabled={chartType === 'genus'}
                    sx={{ m: 0 }}
                  />
                </MuiTooltip>
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6} md="auto">
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', pl: 1 }}>{t('analysis:comparison.sortBy.title')}</Typography>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select value={sortKey} onChange={handleSortChange}>
                  <MenuItem value="subfamily_name">{t('analysis:comparison.sortBy.alphabetical')}</MenuItem>
                  <MenuItem value="db_count">{t('analysis:comparison.sortBy.thisDbCount')}</MenuItem>
                  {checklistSource !== 'all' && <MenuItem value="checklist_count">{t('analysis:comparison.sortBy.checklistCount')}</MenuItem>}
                  {checklistSource !== 'all' && <MenuItem value="difference">{t('analysis:comparison.sortBy.difference')}</MenuItem>}
                </Select>
              </FormControl>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
        <Tabs value={activeFamily} onChange={handleFamilyChange} variant="scrollable" scrollButtons="auto">
          {families.map(family => <Tab key={family} label={family} value={family} />)}
        </Tabs>
      </Box>

      <Box sx={{ mt: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
        ) : error ? (
          <Alert severity="error">{t('analysis:comparison.loadError', { error })}</Alert>
        ) : (
          families.length > 0 ? (
            <Box sx={{ pr: 1 }}>{renderChart()}</Box>
          ) : <Alert severity="info">{t('analysis:comparison.noData')}</Alert>
        )}
      </Box>
    </Paper>
  );
};

export default SubfamilyComparisonChart;